/**
 * PhoneInput — shared types, Zod prop schema, styling constants and the
 * US-number formatting helpers.
 *
 * One field shell holding a fixed "+1", a short divider and a floating-label
 * text input. US numbers only: there is no country picker. The member types
 * national digits; the field shows them as "(AAA) EEE-NNNN" and reports the
 * bare digits.
 *
 * Source: Figma file hk9NaJrKRwkoGmmZ5rxbsE, page v7, "Web/Phone input"
 * (node 69:191). The v7 sheet was drawn from this package's code rather than
 * the UI Design System library, so its variable names stand until design adds
 * them to the library.
 */

import { z } from 'zod';
import { colors } from '../../elements/colors';
import { sizing } from '../../elements/sizing';
import { spacing } from '../../elements/spacing';
import { states } from '../../elements/states';
import { typography } from '../../elements/typography';
import type { TypeStyle } from '../../elements/typography';
import { TEXT_INPUT_BORDER_RADIUS, TEXT_INPUT_HEIGHT } from '../TextInput/TextInput';

/** Catalog-facing schema: data props only; callbacks are platform props. */
export const phoneInputPropsSchema = z.object({
  label: z.string(),
  /** National digits only (at most 10), e.g. "2025550143". */
  value: z.string().optional(),
  errorText: z.string().optional(),
  disabled: z.boolean().default(false),
  testID: z.string().optional(),
  accessibilityLabel: z.string().optional(),
});

export type PhoneInputProps = z.infer<typeof phoneInputPropsSchema>;

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

/** The fixed country-code marker. A numeric format marker, not copy. */
export const PHONE_INPUT_PREFIX = '+1';
export const PHONE_INPUT_MAX_DIGITS = 10;

/**
 * Reduces any typed, pasted or autofilled text to at most 10 US national
 * digits. A leading "1" is dropped only when it is clearly the country code:
 * the text starts with "+1", or it carries more digits than a national number.
 */
export function phoneNationalDigits(text: string): string {
  let digits = text.replace(/\D/g, '');
  const hasCountryCode =
    text.trimStart().startsWith(PHONE_INPUT_PREFIX) || digits.length > PHONE_INPUT_MAX_DIGITS;
  if (hasCountryCode && digits.startsWith('1')) digits = digits.slice(1);
  return digits.slice(0, PHONE_INPUT_MAX_DIGITS);
}

/** Formats national digits as "(AAA) EEE-NNNN", partially while typing. */
export function formatPhoneNational(digits: string): string {
  if (digits === '') return '';
  const area = digits.slice(0, 3);
  const exchange = digits.slice(3, 6);
  const line = digits.slice(6, PHONE_INPUT_MAX_DIGITS);
  if (digits.length <= 3) return `(${area}`;
  if (digits.length <= 6) return `(${area}) ${exchange}`;
  return `(${area}) ${exchange}-${line}`;
}

export interface PhoneInputChange {
  digits: string;
  display: string;
  /** How many digits sit before the caret, so it can be restored after formatting. */
  digitsBeforeCaret: number;
}

function countDigits(text: string): number {
  return text.replace(/\D/g, '').length;
}

/** Which way a delete key removes: Backspace removes backward, Delete forward. */
export type PhoneInputDeleteDirection = 'backward' | 'forward';

interface PhoneInputReplacement {
  /** Where the replaced range starts in the previous text. */
  start: number;
  /** Where the replaced range ends in the previous text. */
  end: number;
  /** What the edit put in its place. */
  text: string;
}

/**
 * The range of the previous text an edit replaced, and what replaced it. The
 * text after the caret is unchanged, so the range ends where that tail starts,
 * and it starts after the longest prefix both texts share. When the new text
 * begins with what was selected, that shared part counts as kept, which gives
 * the same digits as replacing it.
 */
function replacedRange(
  previous: string,
  next: string,
  caret: number,
): PhoneInputReplacement | null {
  const tail = next.slice(caret);
  if (!previous.endsWith(tail)) return null;
  const end = previous.length - tail.length;
  let start = 0;
  while (start < end && start < caret && previous[start] === next[start]) start += 1;
  return { start, end, text: next.slice(start, caret) };
}

/**
 * Turns one edit of the displayed text into the new digits and display.
 * An edit that would take the number past 10 digits keeps every digit it did
 * not replace and only the new digits that fit, so a digit typed into a full
 * number is refused rather than pushing the last digit off.
 * Deleting only a formatting character ("(", ")", " ", "-") would leave the
 * digits unchanged and the character would reappear, so in that case the
 * digit on the deleting side of the caret is removed instead: the one before
 * it for Backspace, the one after it for Delete. `direction` is set only for
 * an edit made by a delete key, so a paste that happens to keep the same
 * digits is left alone.
 */
export function applyPhoneInputEdit(
  previousDisplay: string,
  nextText: string,
  caret: number = nextText.length,
  direction?: PhoneInputDeleteDirection,
): PhoneInputChange {
  const previousDigits = phoneNationalDigits(previousDisplay);

  const replacement = replacedRange(previousDisplay, nextText, caret);
  if (replacement) {
    const before = countDigits(previousDisplay.slice(0, replacement.start));
    const removed = countDigits(previousDisplay.slice(replacement.start, replacement.end));
    const remaining = previousDigits.length - removed;
    const room = PHONE_INPUT_MAX_DIGITS - remaining;
    const inserted = replacement.text.replace(/\D/g, '');
    const candidate =
      previousDigits.slice(0, before) + inserted + previousDigits.slice(before + removed);
    // No US area code starts with 1, so a leading 1 on an 11-digit number is
    // the country code (a member who dials "1" first) and is dropped rather
    // than refusing the last digit.
    if (
      remaining > 0 &&
      candidate.length === PHONE_INPUT_MAX_DIGITS + 1 &&
      candidate.startsWith('1')
    ) {
      const digits = candidate.slice(1);
      return {
        digits,
        display: formatPhoneNational(digits),
        digitsBeforeCaret: before + inserted.length - 1,
      };
    }
    // An edit that leaves no earlier digit is a fresh number, so it takes the
    // path below, where a pasted or autofilled "+1" country code is stripped.
    if (remaining > 0 && inserted.length > room) {
      const added = inserted.slice(0, room);
      const digits =
        previousDigits.slice(0, before) + added + previousDigits.slice(before + removed);
      return {
        digits,
        display: formatPhoneNational(digits),
        digitsBeforeCaret: before + added.length,
      };
    }
  }

  let digits = phoneNationalDigits(nextText);
  let digitsBeforeCaret = Math.min(countDigits(nextText.slice(0, caret)), digits.length);

  const deletedOnlyFormatting =
    nextText.length < previousDisplay.length && digits === previousDigits;
  if (deletedOnlyFormatting && direction === 'forward') {
    digits = digits.slice(0, digitsBeforeCaret) + digits.slice(digitsBeforeCaret + 1);
  } else if (deletedOnlyFormatting && direction === 'backward' && digitsBeforeCaret > 0) {
    digits = digits.slice(0, digitsBeforeCaret - 1) + digits.slice(digitsBeforeCaret);
    digitsBeforeCaret -= 1;
  }

  return { digits, display: formatPhoneNational(digits), digitsBeforeCaret };
}

/** The caret index in `display` just after its `count`-th digit. */
export function phoneCaretIndex(display: string, count: number): number {
  if (count <= 0) return 0;
  let seen = 0;
  for (let index = 0; index < display.length; index += 1) {
    if (/\d/.test(display[index] ?? '')) {
      seen += 1;
      if (seen === count) return index + 1;
    }
  }
  return display.length;
}

// ---------------------------------------------------------------------------
// Styling constants — all derived from design-system elements
// ---------------------------------------------------------------------------

export const PHONE_INPUT_HEIGHT = TEXT_INPUT_HEIGHT;
export const PHONE_INPUT_BORDER_RADIUS = TEXT_INPUT_BORDER_RADIUS;
export const PHONE_INPUT_BORDER_WIDTH = states.borderWidth.default;
export const PHONE_INPUT_HORIZONTAL_PADDING = spacing.m;
/** Gap between the prefix, the divider and the text column. */
export const PHONE_INPUT_SHELL_GAP = spacing.sm;
export const PHONE_INPUT_LABEL_VALUE_GAP = spacing.xxs;
export const PHONE_INPUT_ERROR_GAP = spacing.xs;
export const PHONE_INPUT_ERROR_INDENT = spacing.m;
export const PHONE_INPUT_DIVIDER_WIDTH = states.borderWidth.default;
// v7 draws the divider 24 high with no sizing variant; the 24 icon size holds
// the same value until one is added.
export const PHONE_INPUT_DIVIDER_HEIGHT = sizing.icon.sm;

export const PHONE_INPUT_VALUE_TYPOGRAPHY: TypeStyle = typography.b2Regular;
export const PHONE_INPUT_RESTING_LABEL_TYPOGRAPHY: TypeStyle = typography.b2Regular;
export const PHONE_INPUT_FLOATING_LABEL_TYPOGRAPHY: TypeStyle = typography.c2Regular;

/** The label floats to the top once the field is focused or holds digits. */
export function isPhoneInputLabelFloating(digits: string, focused: boolean): boolean {
  return focused || digits !== '';
}

// ---------------------------------------------------------------------------
// Colour helpers
// ---------------------------------------------------------------------------

export interface PhoneInputState {
  disabled: boolean;
  hasError: boolean;
  focused: boolean;
  hovered: boolean;
}

export interface PhoneInputColors {
  background: string;
  border: string;
  prefix: string;
  divider: string;
  label: string;
  value: string;
}

export function phoneInputColorsFor({
  disabled,
  hasError,
  focused,
  hovered,
}: PhoneInputState): PhoneInputColors {
  // v7's `stroke/alert` error border; `button.critical` holds the same colour
  // as `stroke.alert`.
  const border = disabled
    ? colors.stroke.divider
    : hasError
      ? colors.button.critical
      : focused || hovered
        ? colors.stroke.primary
        : colors.stroke.input;
  const content = disabled ? colors.text.tertiary : colors.text.primary;

  return {
    background: disabled ? colors.background.secondary : colors.background.container,
    border,
    prefix: content,
    divider: colors.stroke.divider,
    label: disabled ? colors.text.tertiary : colors.text.secondary,
    value: content,
  };
}
